import React, { useCallback, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import dayjs from "dayjs";
import { ReactComponent as CalendarIcon } from "@assets/images/icons/calendar.svg";
import ArrowRightIcon from "@assets/icons/ArrowRightIcon";
import { BaseDropdown } from "@components/library/UI";
import { useTranslation } from "@helpers/translate";

import {
  computeRangeAfterClick,
  formatTxPickerDisplayDate,
} from "./transactionDateRangePicker.helpers";
import { CalendarPanel } from "./TransactionDateRangePicker.views";

import "./TransactionDateRangePicker.sass";

const DESKTOP_MIN_WIDTH = 1000;

function TxDateRangeDisplay({ fromText, toText }) {
  return (
    <span className="tx-date-range-picker__range-display">
      <span className="tx-date-range-picker__range-from">{fromText}</span>
      <span className="tx-date-range-picker__range-arrow" aria-hidden>
        <ArrowRightIcon />
      </span>
      <span className="tx-date-range-picker__range-to">{toText}</span>
    </span>
  );
}

function createInitialLeftPanel() {
  return { viewMode: "days", viewMonth: dayjs().startOf("month") };
}

function createInitialRightPanel() {
  return {
    viewMode: "days",
    viewMonth: dayjs().startOf("month").add(1, "month"),
  };
}

function createInitialMobilePanel() {
  return { viewMode: "days", viewMonth: dayjs().startOf("month") };
}

function PickerPanel({ range, onApply, closeDropdown, isDesktop }) {
  const { __ } = useTranslation();
  const [pendingFrom, setPendingFrom] = useState(() => range?.fromDate ?? null);
  const [pendingTo, setPendingTo] = useState(() => range?.toDate ?? null);
  const [leftPanel, setLeftPanel] = useState(createInitialLeftPanel);
  const [rightPanel, setRightPanel] = useState(createInitialRightPanel);
  const [mobilePanel, setMobilePanel] = useState(createInitialMobilePanel);

  const handleDayClick = useCallback(
    (clicked) => {
      const next = computeRangeAfterClick(pendingFrom, pendingTo, clicked);
      setPendingFrom(next.from);
      setPendingTo(next.to);
    },
    [pendingFrom, pendingTo],
  );

  const minNavMonth = dayjs().subtract(10, "year").startOf("month");
  const maxNavMonth = dayjs().add(5, "year").startOf("month");

  const handleConfirm = () => {
    if (!pendingFrom || !pendingTo) return;
    onApply({ fromDate: pendingFrom, toDate: pendingTo });
    closeDropdown();
  };

  const hasCompleteRange = Boolean(pendingFrom && pendingTo);
  const canConfirm = hasCompleteRange;

  return (
    <div className="tx-date-range-picker">
      <div className="tx-date-range-picker__body">
        {isDesktop ? (
          <>
            <CalendarPanel
              panel={leftPanel}
              setPanel={setLeftPanel}
              pendingFrom={pendingFrom}
              pendingTo={pendingTo}
              onDayClick={handleDayClick}
              showNav={false}
              minNavMonth={minNavMonth}
              maxNavMonth={maxNavMonth}
            />
            <CalendarPanel
              panel={rightPanel}
              setPanel={setRightPanel}
              pendingFrom={pendingFrom}
              pendingTo={pendingTo}
              onDayClick={handleDayClick}
              showNav={false}
              minNavMonth={minNavMonth}
              maxNavMonth={maxNavMonth}
            />
          </>
        ) : (
          <CalendarPanel
            panel={mobilePanel}
            setPanel={setMobilePanel}
            pendingFrom={pendingFrom}
            pendingTo={pendingTo}
            onDayClick={handleDayClick}
            showNav
            minNavMonth={minNavMonth}
            maxNavMonth={maxNavMonth}
          />
        )}
      </div>

      <div className="tx-date-range-picker__footer">
        <div className="tx-date-range-picker__footer-title">
          {__("wallet.date_range_selected")}:{" "}
          {pendingFrom ? (
            formatTxPickerDisplayDate(pendingFrom)
          ) : (
            <span className="tx-date-range-picker__footer-dates-placeholder">
              ..
            </span>
          )}{" "}
          {__("wallet.to")}{" "}
          {pendingTo ? (
            formatTxPickerDisplayDate(pendingTo)
          ) : (
            <span className="tx-date-range-picker__footer-dates-placeholder">
              ..
            </span>
          )}
        </div>
        <button
          type="button"
          className="tx-date-range-picker__confirm"
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          {__("common.confirm")}
        </button>
      </div>
    </div>
  );
}

/**
 * Wallet transaction history date range filter (Binance-style).
 * @param {{ fromDate: import("dayjs").Dayjs | null, toDate: import("dayjs").Dayjs | null }} range
 * @param {(payload: { fromDate: import("dayjs").Dayjs, toDate: import("dayjs").Dayjs }) => void} onApply
 */
export const TransactionDateRangePicker = ({ range, onApply }) => {
  const { __ } = useTranslation();
  const isDesktop = useMediaQuery(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);

  const hasAppliedRange = Boolean(range?.fromDate && range?.toDate);

  return (
    <BaseDropdown
      className="tx-date-range-picker-dropdown"
      align={isDesktop ? "right" : "center"}
      useMenuPortal
      menuClassName="tx-date-range-picker__menu-root"
      trigger={
        <button type="button" className="tx-date-range-picker__trigger">
          {hasAppliedRange ? (
            <TxDateRangeDisplay
              fromText={formatTxPickerDisplayDate(range.fromDate)}
              toText={formatTxPickerDisplayDate(range.toDate)}
            />
          ) : (
            <span className="tx-date-range-picker__trigger-label">
              {__("wallet.date_range_filter")}
            </span>
          )}
          <CalendarIcon aria-hidden />
        </button>
      }
    >
      {({ closeDropdown }) => (
        <PickerPanel
          key={`${range?.fromDate?.valueOf() ?? "x"}-${
            range?.toDate?.valueOf() ?? "y"
          }`}
          range={range}
          onApply={onApply}
          closeDropdown={closeDropdown}
          isDesktop={isDesktop}
        />
      )}
    </BaseDropdown>
  );
};

export default TransactionDateRangePicker;
